import React from "react";
import { Modal, Button, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { dateTimeFormatter, euroFormatter } from "src/global";
import { linkBuilder } from "src/global/navigationHelper";

const InvoiceDetailsDialog = ({ invoice, handleClose }) => {
  const { t } = useTranslation();

  return (
    <Modal show={true} onHide={handleClose} size="lg">
      <Modal.Header>
        <Modal.Title>
          {t("accounting.invoice_details_dialog.title")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {invoice ? (
          <>
            <Table bordered>
              <tbody>
                <tr>
                  <th>
                    {t(
                      "accounting.invoice_details_dialog.fields.invoice_number"
                    )}
                  </th>
                  <td>{invoice.id}</td>
                </tr>
                <tr>
                  <th>
                    {t("accounting.invoice_details_dialog.fields.created_at")}
                  </th>
                  <td>{dateTimeFormatter(invoice.created_at)}</td>
                </tr>
                <tr>
                  <th>
                    {t("accounting.invoice_details_dialog.fields.paid_at")}
                  </th>
                  <td>
                    {invoice.paid_at ? dateTimeFormatter(invoice.paid_at) : "-"}
                  </td>
                </tr>
                <tr>
                  <th>
                    {t("accounting.invoice_details_dialog.fields.status")}
                  </th>
                  <td>
                    {invoice.status === "paid" ? (
                      <span className="badge bg-success text-white">
                        {t(
                          "accounting.invoice_details_dialog.status_labels.paid"
                        )}
                      </span>
                    ) : (
                      <span className="badge bg-secondary text-white">
                        {t(
                          "accounting.invoice_details_dialog.status_labels.unpaid"
                        )}
                      </span>
                    )}
                  </td>
                </tr>
                <tr>
                  <th>{t("accounting.invoice_details_dialog.fields.total")}</th>
                  <td>{euroFormatter(invoice.total_sum)}</td>
                </tr>
                <tr>
                  <th>{t("accounting.invoice_details_dialog.fields.paid")}</th>
                  <td>
                    {euroFormatter(
                      invoice.payments.reduce((sum, p) => sum + p.amount, 0)
                    )}
                  </td>
                </tr>
                <tr>
                  <th>{t("accounting.invoice_details_dialog.fields.open")}</th>
                  <td>{euroFormatter(invoice.open_amount)}</td>
                </tr>
                <tr>
                  <th>
                    {t("accounting.invoice_details_dialog.fields.receipt")}
                  </th>
                  <td>
                    {invoice.fiscal_printed_at
                      ? dateTimeFormatter(invoice.fiscal_printed_at)
                      : "—"}
                  </td>
                </tr>
                <tr>
                  <th>
                    {t("accounting.invoice_details_dialog.fields.payment_type")}
                  </th>
                  <td>
                    {invoice.formatted_payment_type || invoice.payment_type}
                  </td>
                </tr>
              </tbody>
            </Table>

            {/* Items */}
            {invoice.items && invoice.items.length > 0 && (
              <>
                <h5>{t("accounting.invoice_details_dialog.items.title")}</h5>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>
                        {t(
                          "accounting.invoice_details_dialog.items.description"
                        )}
                      </th>
                      <th>
                        {t("accounting.invoice_details_dialog.items.quantity")}
                      </th>
                      <th>
                        {t(
                          "accounting.invoice_details_dialog.items.unit_price"
                        )}
                      </th>
                      <th>
                        {t(
                          "accounting.invoice_details_dialog.items.total_price"
                        )}
                      </th>
                      <th>
                        {t("accounting.invoice_details_dialog.items.tax")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>{euroFormatter(item.unit_price)}</td>
                        <td>{euroFormatter(item.total_price)}</td>
                        <td>{item.formatted_tax_rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}

            {/* Payments */}
            {invoice.payments && invoice.payments.length > 0 && (
              <>
                <h5>{t("accounting.invoice_details_dialog.payments.title")}</h5>
                <Table size="sm" bordered>
                  <thead>
                    <tr>
                      <th>
                        {t("accounting.invoice_details_dialog.payments.type")}
                      </th>
                      <th>
                        {t("accounting.invoice_details_dialog.payments.amount")}
                      </th>
                      <th>
                        {t("accounting.invoice_details_dialog.payments.date")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p) => (
                      <tr key={p.id}>
                        <td>{p.formatted_payment_type || p.payment_type}</td>
                        <td>{euroFormatter(p.amount)}</td>
                        <td>{dateTimeFormatter(p.paid_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </>
        ) : (
          <div className="text-center text-muted">
            {t("accounting.invoice_details_dialog.not_found")}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {t("accounting.invoice_details_dialog.close")}
        </Button>
        <Button
          variant="primary"
          href={linkBuilder(
            `/dashboard/stays/${invoice.stay_id}/invoices/${invoice.id}/edit`
          )}
          target={"_blank"}
        >
          {t("accounting.invoice_details_dialog.edit_invoice")}
        </Button>

        <Button
          variant="primary"
          href={linkBuilder(`/dashboard/stays/${invoice.stay_id}`)}
          target={"_blank"}
        >
          {t("accounting.invoice_details_dialog.to_booking")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceDetailsDialog;
